import { __ } from '@wordpress/i18n';
import { setupLocationVisibility } from './cityFilter';
import { initLocationPicker } from './locationPicker';

const GROUPS = [
	{
		label: __( 'Basic information', 'anacrm' ),
		description: __(
			'Description, keywords, space type, governance.',
			'anacrm'
		),
		open: false,
		fields: [
			'_sp_description',
			'_sp_keyword',
			'_sp_space_type',
			'_sp_use',
			'_sp_governance',
			'_sp_image',
		],
	},
	{
		label: __( 'Location', 'anacrm' ),
		description: __( 'Territory, city, address.', 'anacrm' ),
		fields: [
			'_sp_location',
			'_sp_territory',
			'_sp_region',
			'_sp_city',
			'_sp_address',
			'_sp_lat',
			'_sp_lng',
		],
	},
	{
		label: __( 'Additional information', 'anacrm' ),
		description: __(
			'Access type, capacity, accessibility, available services, opening hours.',
			'anacrm'
		),
		fields: [
			'_sp_access',
			'_sp_capacity',
			'_sp_accesibility',
			'_sp_service',
			'_sp_open_days',
			'_sp_open_shift',
			'_sp_open_details',
			'_sp_target',
		],
	},
	{
		label: __( 'Contact', 'anacrm' ),
		description: __(
			'Contact person, email, phone, website, Social networks.',
			'anacrm'
		),
		fields: [
			'_sp_person_ref',
			'_sp_contact_mail',
			'_sp_contact_phone',
			'_sp_contact_website',
			'_sp_contact_social',
		],
	},
	{
		label: __( 'Relations', 'anacrm' ),
		description: __(
			'Relations with organizations and projects.',
			'anacrm'
		),
		fields: [ '_sp_org', '_sp_project' ],
	},
];

function findFieldWrapper( form, name ) {
	// Pods adds class pods-form-ui-row-name-{name} to every field container.
	// Underscores in the field name are converted to dashes in the class,
	// so _sp_keyword → pods-form-ui-row-name--sp-keyword (double dash from leading _).
	const dashedName = name.replace( /_/g, '-' );
	return form.querySelector( `.pods-form-ui-row-name-${ dashedName }` );
}

const REQUIRED_TEXT_FIELDS = [ 'post_title' ];
const REQUIRED_PICK_FIELDS = [ '_sp_space_type', '_sp_territory', '_sp_city' ];

function markFieldRequired( wrapper ) {
	// Front-end Pods forms render fields in "embed" mode, which skips the
	// admin-only DFV FieldLabel React component entirely (no
	// .pods-field-label__* markup exists here). The visible label is
	// server-rendered PHP instead (PodsForm::label(), ui/fields/_label.php),
	// with class "pods-form-ui-label" — that's what we mark up here, instead
	// of setting the field's `required` option in PHP (which would also
	// switch on Pods' native required-validation UI and produce a second,
	// redundant error message alongside setupRequiredFieldsValidation()
	// below).
	const label = wrapper?.querySelector( '.pods-form-ui-label' );
	if ( ! label || label.querySelector( '.anacrm-required-marker' ) ) {
		return;
	}
	const marker = document.createElement( 'abbr' );
	marker.className = 'anacrm-required-marker text-red no-underline';
	marker.title = __( 'required', 'anacrm' );
	marker.textContent = ' *';
	label.appendChild( marker );
}

function hasTextValue( form, fieldName ) {
	const input = form.querySelector(
		`input[name^="pods_field_${ fieldName }"]`
	);
	return Boolean( input && input.value.trim() );
}

function hasPickValue( form, fieldName ) {
	// Pods (DFV/react-select) renders one hidden input per selected term,
	// named pods_field_{field}[0], [1], ... and renders none at all when
	// the field is empty — there is no single always-present value holder.
	return (
		form.querySelectorAll(
			`input[type="hidden"][name^="pods_field_${ fieldName }["]`
		).length > 0
	);
}

// _sp_territory (and, depending on the territory picked, _sp_city) are
// hidden by setupLocationVisibility() — via inline style.display — for
// spaces with no physical location, or, for city, spaces located purely in
// the Peninsula (region is used instead there). A hidden field isn't
// something the user can fill in, so it's exempt from the required check
// for as long as it stays hidden.
function isFieldApplicable( form, name ) {
	const wrapper = findFieldWrapper( form, name );
	return ! wrapper || wrapper.style.display !== 'none';
}

function isRequiredFieldsValid( form ) {
	return (
		REQUIRED_TEXT_FIELDS.every(
			( name ) =>
				! isFieldApplicable( form, name ) || hasTextValue( form, name )
		) &&
		REQUIRED_PICK_FIELDS.every(
			( name ) =>
				! isFieldApplicable( form, name ) || hasPickValue( form, name )
		)
	);
}

function firstMissingRequiredField( form ) {
	return (
		REQUIRED_TEXT_FIELDS.find(
			( name ) =>
				isFieldApplicable( form, name ) && ! hasTextValue( form, name )
		) ??
		REQUIRED_PICK_FIELDS.find(
			( name ) =>
				isFieldApplicable( form, name ) && ! hasPickValue( form, name )
		)
	);
}

function setupRequiredFieldsValidation( form ) {
	let errorEl = null;

	function showError() {
		if ( errorEl ) {
			return;
		}
		errorEl = document.createElement( 'div' );
		errorEl.className = 'w-full px-4 py-3 mb-4 bg-red text-black';
		errorEl.setAttribute( 'role', 'alert' );
		errorEl.textContent = __(
			'Please fill in name, space type, territory and city.',
			'anacrm'
		);
		form.insertBefore( errorEl, form.firstChild );
	}

	function clearError() {
		if ( errorEl ) {
			errorEl.remove();
			errorEl = null;
		}
	}

	function block( e ) {
		e.preventDefault();
		e.stopPropagation();
		e.stopImmediatePropagation();
		showError();
		findFieldWrapper(
			form,
			firstMissingRequiredField( form )
		)?.scrollIntoView( { block: 'center' } );
	}

	// Plain text fields (post_title) dispatch a native "input" event; the
	// pick fields below don't (React adds/removes hidden inputs instead), so
	// a MutationObserver is what notices those changes and clears a shown
	// error live.
	form.addEventListener( 'input', ( e ) => {
		const name = e.target.getAttribute( 'name' ) || '';
		if (
			REQUIRED_TEXT_FIELDS.some( ( f ) =>
				name.startsWith( `pods_field_${ f }` )
			) &&
			errorEl &&
			isRequiredFieldsValid( form )
		) {
			clearError();
		}
	} );

	const observer = new MutationObserver( () => {
		if ( errorEl && isRequiredFieldsValid( form ) ) {
			clearError();
		}
	} );
	observer.observe( form, { childList: true, subtree: true } );

	// Same two-listener capture-phase pattern used elsewhere: Pods binds its
	// submit-button click handler with jQuery delegation in the bubble
	// phase and replays it as a synthetic jQuery-only submit that never
	// dispatches a real "submit" event, so a capture-phase click on the
	// submit control is needed in addition to a capture-phase submit
	// listener (the fallback for Enter-key submission, which does dispatch
	// a real native "submit" event).
	form.addEventListener(
		'click',
		( e ) => {
			if (
				! e.target.closest(
					'input[type="submit"], button[type="submit"]'
				)
			) {
				return;
			}
			if ( isRequiredFieldsValid( form ) ) {
				clearError();
				return;
			}
			block( e );
		},
		true
	);

	form.addEventListener(
		'submit',
		( e ) => {
			if ( isRequiredFieldsValid( form ) ) {
				clearError();
				return;
			}
			block( e );
		},
		true
	);
}

function buildAccordion( form ) {
	for ( const group of GROUPS ) {
		const fieldWrappers = group.fields
			.map( ( f ) => findFieldWrapper( form, f ) )
			.filter( Boolean );
		if ( ! fieldWrappers.length ) {
			continue;
		}

		const parent = fieldWrappers[ 0 ].parentElement;

		const details = document.createElement( 'details' );
		if ( group.open ) {
			details.open = true;
		}
		details.className = 'mb-4 border-2 border-green';

		const summary = document.createElement( 'summary' );
		summary.className =
			'px-4 py-3 cursor-pointer select-none bg-green/30 hover:bg-green/60 marker:float-left';
		const summaryTit = document.createElement( 'h2' );
		summaryTit.className = 'text-base font-semibold inline-block';
		summaryTit.textContent = group.label;
		summary.appendChild( summaryTit );
		const summaryDesc = document.createElement( 'p' );
		summaryDesc.textContent = group.description ?? '';
		summary.appendChild( summaryDesc );
		details.appendChild( summary );

		const body = document.createElement( 'div' );
		body.className = 'px-4 py-2 bg-green/10';
		details.appendChild( body );

		parent.insertBefore( details, fieldWrappers[ 0 ] );
		fieldWrappers.forEach( ( w ) => body.appendChild( w ) );
	}
}

document.addEventListener( 'DOMContentLoaded', () => {
	const wrapper = document.querySelector( '.anacrm-space-form-accordion' );
	if ( ! wrapper ) {
		return;
	}

	const successDiv = wrapper.querySelector( '.pods-form-front-success' );
	if ( successDiv ) {
		const btn = document.createElement( 'button' );
		btn.type = 'button';
		btn.className = 'px-3 py-2 text-2xl hover:text-yellow cursor-pointer';
		btn.innerHTML = '&times;';
		btn.addEventListener( 'click', () => successDiv.remove() );
		successDiv.appendChild( btn );
	}

	const form = wrapper.querySelector( 'form' );
	if ( ! form ) {
		return;
	}

	setupRequiredFieldsValidation( form );

	let done = false;

	function tryBuild() {
		if ( done ) {
			return;
		}
		if ( ! form.querySelector( '.pods-field__container' ) ) {
			return;
		}
		done = true;
		observer.disconnect();
		buildAccordion( form );
		[ ...REQUIRED_TEXT_FIELDS, ...REQUIRED_PICK_FIELDS ].forEach(
			( name ) => markFieldRequired( findFieldWrapper( form, name ) )
		);
		setupLocationVisibility( form, {
			locationField: '_sp_location',
			territoryField: '_sp_territory',
			regionField: '_sp_region',
			cityField: '_sp_city',
			addressField: '_sp_address',
		} );
		initLocationPicker( form, { prefix: '_sp_' } );
	}

	let debounceTimer = null;
	const observer = new MutationObserver( () => {
		clearTimeout( debounceTimer );
		debounceTimer = setTimeout( tryBuild, 300 );
	} );

	observer.observe( form, { childList: true, subtree: true } );

	tryBuild();
} );
